import useBacktestingDrawer from "@shared/features/bots/BotForm/Backtesting/useBacktestingDrawer";
import useQuery from "utils/hooks/useQuery";
import { MOBILE_SEARCH } from "common/constants";
import {
  APPLIED_BACKTESTING_QUERY_PARAM,
  BACKTESTING_BOT_QUERY_PARAM,
} from "features/bots/common/constants";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { sharedLib } from "@shared/services/shared.service";
import { skipToken } from "@reduxjs/toolkit/query";
import BacktestingGraph from "features/bots/BotForm/Backtesting/BacktestingFormItems/BacktestingGraph";
import ActivitiesTable from "features/bots/BotForm/Backtesting/BacktestingFormItems/ActivitiesTable";
import useMedia from "utils/hooks/useMedia";
import { convertToObjectsArray } from "@shared/utils/arrays";
import InProgressMessage from "features/bots/BotForm/Backtesting/common/InProgressMessage";
import Disclaimer from "features/bots/BotForm/Backtesting/common/Disclaimer";
import useLiveBacktesting from "@shared/features/bots/BotForm/Backtesting/useLiveBacktesting";
import { usePrevious } from "react-use";
import { BacktestingStatus, LanguageNameSpaces } from "@shared/types";
import { BotFormItemWrapper } from "features/bots/BotForm/common/styles";
import { CSSProperties } from "react";
import EmptyView from "common/Empty/EmptyView";
import { EmptyStatesType } from "common/Icons/types.ts";
import styled, { useTheme } from "styled-components";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { CalendarDays } from "lucide-react";
import PanelEmpty from "common/Empty/PanelEmpty.tsx";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import BacktestPerformance from "features/bots/BotForm/Backtesting/NewLook/BacktestPerformance";
import {
  formatTimeframe,
  getTimeframeDays,
} from "features/bots/BotForm/Backtesting/NewLook/format";

interface BacktestingFormItemsProps {
  style?: CSSProperties;
}

const BacktestingFormItems = ({ style }: BacktestingFormItemsProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const theme = useTheme();
  const isMobile = useMedia([`(max-width: 900px)`], [true], false);
  const isSmallMobile = useMedia(
    [`(max-width: ${MOBILE_SEARCH})`],
    [true],
    false
  );
  const backtestSmallScreenLayout = useMedia(
    [`(max-width: 1350px)`],
    [true],
    false
  );
  const query = useQuery();
  const backtestingIdStr = query.get(APPLIED_BACKTESTING_QUERY_PARAM);
  const backtestingBotIdStr = query.get(BACKTESTING_BOT_QUERY_PARAM);
  const backtestingId = backtestingIdStr
    ? parseInt(backtestingIdStr)
    : undefined;
  const backtestingBotId = backtestingBotIdStr
    ? parseInt(backtestingBotIdStr)
    : undefined;
  const prevBacktestingId = usePrevious(backtestingId);
  const shouldInitializePageNumber = backtestingId !== prevBacktestingId;

  const { backtestingRun, loading, backtestingJobResults } =
    useBacktestingDrawer({
      backtestingBotId,
      backtestingId,
    });
  const { status, inProgress, liveBacktestingData } = useLiveBacktesting({
    data: backtestingRun,
  });

  const { data: activities, isFetching: activitiesFetching } =
    sharedLib.sharedApi.bots.useGetBacktestingTradesActivitiesQuery(
      backtestingId ? { backtesting_run_id: backtestingId } : skipToken,
      { refetchOnMountOrArgChange: true }
    );
  const jobActivitiesData = backtestingJobResults?.activities_data;
  const jobActivities = jobActivitiesData
    ? convertToObjectsArray(jobActivitiesData?.data, jobActivitiesData?.headers)
    : undefined;

  const isLoading = loading || activitiesFetching;

  const emptyState = (
    <EmptyView
      iconKey={EmptyStatesType.Graph}
      iconWidth={140}
      iconStyle={{ marginBottom: 12 }}
      description={t(
        "backtesting.empty_states.apply_configuration_description",
        {
          side: backtestSmallScreenLayout
            ? t("backtesting.empty_states.side_left")
            : t("backtesting.empty_states.side_right"),
        }
      )}
      descriptionStyle={{ maxWidth: 350 }}
    />
  );

  if (status === BacktestingStatus.FAILED) return null;

  if (theme.isNewLook) {
    // New look: the result leads (net PnL over its accumulated profit, the
    // split in a well under it), then the activities; skeletons while it
    // loads, a short empty state until a backtest is applied.
    const days = getTimeframeDays(backtestingRun?.backtesting_time_range);
    // Without a container style the results sit inside the form's own panel
    // (signals form, phones): a section under a hairline, not a nested box.
    const tokens = getNewLookTokens(theme);
    const wrapperStyle = style
      ? { marginTop: 16, padding: 16, minHeight: 100, ...style }
      : {
          marginTop: 24,
          padding: "20px 0 0",
          minHeight: 100,
          border: "none",
          borderTop: `1px solid ${tokens.hairline}`,
          borderRadius: 0,
          boxShadow: "none",
          background: "transparent",
        };
    return (
      <BotFormItemWrapper style={wrapperStyle} $isMobile={isMobile}>
        {backtestingId ? (
          <>
            <Disclaimer />
            {inProgress && <InProgressMessage />}
            <BacktestPerformance
              bare={true}
              compact={true}
              backtestingRun={liveBacktestingData}
              backtestingJobResults={backtestingJobResults}
              loading={loading}
              chartHeight={isSmallMobile ? 168 : 200}
              aside={
                backtestingRun?.backtesting_time_range && (
                  <NewLookPeriod>
                    <CalendarDays size={14} strokeWidth={1.75} />
                    {formatTimeframe(backtestingRun.backtesting_time_range)}
                    {days ? ` · ${days} ${t("backtesting.labels.days")}` : ""}
                  </NewLookPeriod>
                )
              }
            />
            <ActivitiesTable
              activities={jobActivities ?? activities}
              shouldInitializePageNumber={shouldInitializePageNumber}
              inProgress={inProgress}
              loading={isLoading}
            />
          </>
        ) : (
          <PanelEmpty
            title={t("backtesting.empty_states.no_results_title")}
            description={t(
              "backtesting.empty_states.apply_configuration_description",
              { side: backtestSmallScreenLayout ? "left" : "right" }
            )}
          />
        )}
      </BotFormItemWrapper>
    );
  }

  return (
    <BotFormItemWrapper
      style={{
        marginTop: 16,
        padding: isSmallMobile ? 12 : 16,
        position: "relative",
        minHeight: 100,
        ...style,
      }}
      $isMobile={isMobile}
    >
      <LoadingSpinnerOverlay
        isLoading={isLoading}
        hideContentWhileLoading={true}
        backgroundColor={theme.colors.cardBgColor}
      >
        {backtestingId ? (
          <>
            <Disclaimer />
            {inProgress && <InProgressMessage />}
            <BacktestingGraph backtestingRun={backtestingRun} />
            <ActivitiesTable
              activities={jobActivities ?? activities}
              shouldInitializePageNumber={shouldInitializePageNumber}
              inProgress={inProgress}
            />
          </>
        ) : (
          emptyState
        )}
      </LoadingSpinnerOverlay>
    </BotFormItemWrapper>
  );
};

export default BacktestingFormItems;

const NewLookPeriod = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  white-space: nowrap;
`;
